import { Box, Button, Link, Text } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import { Branch } from '@texas/api/endpoints/branchesApi';
import { SupplierComplianceDocument } from '@texas/api/endpoints/supplierComplianceDocumentsApi';
import { Role } from '@texas/api/endpoints/userApi';
import { clientEndpoints } from '@texas/clientEndpoints';
import { RoleRestrict } from '@texas/components/shared/RoleRestrict';
import { SupplierComplianceDocumentBranches } from './SupplierComplianceDocumentBranches';

interface Props {
  t: TFunction;
  onEdit: (document: SupplierComplianceDocument) => void;
  branches: Branch[];
}

const columnHelper = createColumnHelper<SupplierComplianceDocument>();

function formatDocumentDate(value: string | null) {
  return value ? dayjs(value).locale('en').format('DD MMM YYYY') : '—';
}

export function supplierComplianceDocumentColumns({
  t,
  onEdit,
  branches,
}: Props) {
  return [
    columnHelper.accessor(
      (document) => document.label ?? document.file.originalName,
      {
        id: 'document',
        cell: (info) => (
          <Box>
            <Text fontWeight="semibold">{info.getValue()}</Text>
            {info.row.original.archived && (
              <Text fontSize="12px" variant="entity-directory-description">
                {t('general.archived')}
              </Text>
            )}
            <Link
              href={clientEndpoints.downloadFile(
                info.row.original.file.identifier,
              )}
              display="block"
              mt="5px"
              variant="entityDirectoryMuted"
            >
              {info.row.original.file.originalName}
            </Link>
          </Box>
        ),
        enableSorting: false,
        header: t('suppliers.workspace.document'),
        size: 220,
      },
    ),
    columnHelper.accessor('description', {
      cell: (info) => info.getValue() ?? '—',
      enableSorting: false,
      header: t('general.description'),
      size: 170,
    }),
    columnHelper.display({
      id: 'branches',
      cell: (info) => (
        <SupplierComplianceDocumentBranches
          branches={branches}
          document={info.row.original}
        />
      ),
      enableSorting: false,
      header: t('general.branches'),
      size: 250,
    }),
    columnHelper.accessor('startDate', {
      cell: (info) => formatDocumentDate(info.getValue()),
      enableSorting: false,
      header: t('suppliers.workspace.startDate'),
      size: 110,
    }),
    columnHelper.accessor('endDate', {
      cell: (info) => formatDocumentDate(info.getValue()),
      enableSorting: false,
      header: t('suppliers.workspace.endDate'),
      size: 110,
    }),
    columnHelper.display({
      id: 'actions',
      cell: (info) => (
        <RoleRestrict
          allow={[Role.SystemAdministrator, Role.InternalAdministrator]}
        >
          <Button
            size="sm"
            variant="entityDirectorySecondary"
            onClick={() => onEdit(info.row.original)}
          >
            {t('general.edit')}
          </Button>
        </RoleRestrict>
      ),
      enableSorting: false,
      header: t('general.actions'),
      size: 90,
    }),
  ];
}
